import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { device } from 'utils';
import { buildDonationUrl } from '../utils/utm';
import moment from 'moment';
import ShareButton from '../Share/ShareButton';
import { useIsMobile } from '../helpers/useIsMobile';
import LikeButton from '../Like/LikeButton';
import { createPortal } from 'react-dom';

export default function Support({
  projectId,
  projectStatus,
  project,
  ...utmParams
}) {
  const isMobile = useIsMobile();
  const buttonRef = useRef(null);
  const [showStickyBar, setShowStickyBar] = useState(false);

  const isFinished =
    projectStatus === 'successful' || projectStatus === 'finished';

  const isTicketable = Boolean(project?.is_ticketable);

  const donationUrl = useMemo(() => {
    return buildDonationUrl({
      projectId,
      donationValue: '30,00',
      utmParams,
    });
  }, [projectId, utmParams]);

  useEffect(() => {
    if (!isMobile || isFinished || !buttonRef.current) {
      setShowStickyBar(false);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        setShowStickyBar(!entry.isIntersecting);
      },
      {
        threshold: 0.1,
      }
    );

    observer.observe(buttonRef.current);

    return () => observer.disconnect();
  }, [isMobile, isFinished, isTicketable]);

  const scrollToRewards = () => {
    const targetElement = document.getElementById('rewards-section');

    if (!targetElement) return;

    window.scrollTo({
      top: targetElement.offsetTop - 100,
      behavior: 'smooth',
    });
  };

  const supportConfig = getSupportConfig({
    isFinished,
    isTicketable,
    donationUrl,
    scrollToRewards,
    isNotSuccess: projectStatus === 'finished',
  });

  const dateLabel = isTicketable ? 'Gestartet am:' : 'Projekt gestartet am:';
  const eventLocation = project?.event_location?.trim?.() || '';
  const legacyCity = project?.city || project?.categoryType?.city || '';
  const locationLabel = eventLocation
    ? isTicketable
      ? 'Veranstaltungsort:'
      : 'Wirkungsort:'
    : 'Ort:';
  const location = eventLocation || legacyCity;

  const viewMode = window.location?.pathname.startsWith('/view');
  return (
    <SupportWrapper>
      <Inner>
        <div ref={buttonRef}>
          {supportConfig.disabled || viewMode ? (
            <ProjectFinishedButton type="button" disabled>
              {supportConfig.label}
            </ProjectFinishedButton>
          ) : supportConfig.onClick ? (
            <ActionButton
              type="button"
              onClick={viewMode ? undefined : supportConfig.onClick}
            >
              {supportConfig.label}
            </ActionButton>
          ) : (
            <StyledLink to={viewMode ? undefined : supportConfig.to}>
              {supportConfig.label}
            </StyledLink>
          )}
        </div>

        <UnderButton>
          <UnderButtonItemWrapper>
            <UnderButtonText>
              {dateLabel}{' '}
              {moment
                .unix(Number(project.funding_start_date || project.created_at))
                .format('DD.MM.YYYY')}
            </UnderButtonText>

            {location && (
              <UnderButtonText>
                {locationLabel} {location}
              </UnderButtonText>
            )}
          </UnderButtonItemWrapper>

          {isMobile && (
            <MobileActions>
              <ShareButton project={project} viewMode={viewMode} />
              <LikeButton project={project} viewMode={viewMode} />
            </MobileActions>
          )}
        </UnderButton>

        {showStickyBar
          ? createPortal(
            <StickyMobileBar>
              <StickyShareButton>
                <ShareButton
                  project={project}
                  height={48}
                  viewMode={viewMode}
                />
              </StickyShareButton>

              {viewMode ? (
                <ProjectFinishedButton type="button" disabled>
                  {supportConfig.label}
                </ProjectFinishedButton>
              ) : supportConfig.onClick ? (
                <ActionButton
                  type="button"
                  onClick={supportConfig.onClick}
                >
                  {supportConfig.label}
                </ActionButton>
              ) : (
                <StyledLink to={supportConfig.to}>
                  {supportConfig.label}
                </StyledLink>
              )}
            </StickyMobileBar>,
            document.body
          )
          : null}
      </Inner>
    </SupportWrapper>
  );
}

function getSupportConfig({
  isFinished,
  isTicketable,
  donationUrl,
  scrollToRewards,
  isNotSuccess,
}) {
  if (isFinished && isNotSuccess) {
    return {
      disabled: true,
      label: isTicketable ? 'Event beendet' : 'Projekt beendet',
    };
  }

  if (isFinished) {
    return {
      disabled: true,
      label: isTicketable
        ? 'Der Zeitraum für den Ticketkauf ist leider abgelaufen.'
        : 'Projekt erfolgreich abgeschlossen',
    };
  }

  if (isTicketable) {
    return {
      label: 'Ticket buchen',
      onClick: scrollToRewards,
    };
  }

  return {
    label: 'Jetzt unterstützen!',
    to: donationUrl,
  };
}

const SupportWrapper = styled.div`
  width: 100%;
  margin-bottom: 20px;
`;

const Inner = styled.div`
  border-radius: 8px;
`;

const UnderButton = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-top: 12px;
  align-content: center;
`;

const UnderButtonItemWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  flex: 1;

  @media (max-width: 720px) {
    flex-direction: column;
    text-align: left;
    align-items: flex-start;
    gap: 4px;
  }
`;

const UnderButtonText = styled.span`
  font-family: Inter, sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: #535862;
`;

const MobileActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const actionStyles = `
  width: 100%;
  background: #9dbc60;
  font-family: Inter, sans-serif;
  border: 2px solid #9dbc60;
  color: #ffffff !important;
  text-align: center;
  border-radius: 8px;
  cursor: pointer;
  padding: 11px 18px;
  font-size: 16px;
  display: block;
  margin-bottom: 0;
  font-weight: bold;
  height: 48px;
  text-decoration: none;
  transition: background 0.2s ease, color 0.2s ease;
  &:hover {
    color: #ffffff !important;
    background: #3f621a;
  }

  &:active {
  
    background: #3f621a;
  
    color: #ffffff !important;
  
  }

  @media ${device.laptop} {
    font-size: 16px;
    padding: 11px 18px;
  }

  @media ${device.allMobile} {
    font-size: 16px !important;
    padding: 10px 18px;
  }
`;

const StyledLink = styled(Link)`
  ${actionStyles}
`;

const ActionButton = styled.button`
  ${actionStyles}
`;

const ProjectFinishedButton = styled.button`
  box-sizing: border-box;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 12px 18px;
  gap: 6px;
  width: 100%;
  height: 48px;

  background: #ffffff;
  border: 1px solid #9dbc60;
  border-radius: 8px;

  font-family: Inter, sans-serif;
  font-weight: 600;
  font-size: 16px;
  line-height: 24px;

  cursor: default;
  color: #9dbc60;
`;

const StickyMobileBar = styled.div`
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 999;
  display: none;
  gap: 10px;
  padding: 20px 16px calc(20px + env(safe-area-inset-bottom));
  background: #ffffff;
  box-shadow: 0 -4px 16px rgba(0, 0, 0, 0.12);

  @media ${device.allMobile} {
    display: flex;
  }
`;

const StickyShareButton = styled.div`
  flex: 0 0 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StickyDonateLink = styled(Link)`
  ${actionStyles}
  flex: 1;
  height: 48px;
  margin-bottom: 0;
`;

Support.propTypes = {
  projectId: PropTypes.number,
  projectStatus: PropTypes.string,
  donationsAverage: PropTypes.string,
  project: PropTypes.object.isRequired,
  handleShowContactModal: PropTypes.func,
};
